import SafeReturnForm from 'components/patient/SafeReturnForm';
import { ActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import StandardModal, { PatientName } from 'components/shared/StandardModal';
import { FormProvider } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import { useProfile } from 'context/profile';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import LocationEpisode from 'models/LocationEpisode';
import { ReviewStatus } from 'models/Review';
import SafeReturn from 'models/reviews/SafeReturn';
import { updateReview } from 'services/api/reviews';
import { BodySmall } from 'styles/typography';

type AcknowledgeSafeReturnModalProps = {
  onCancel: () => void;
  onSuccess: () => void;
  locationEpisode: LocationEpisode;
  patientName: string;
  review: SafeReturn;
};

export type AcknowledgeSafeReturnFormValues = {
  note: ActivityInputValues;
  groupId: string;
};

export type AcknowledgeSafeReturnFormMeta = {
  isSubmitting: boolean;
  isUploadingAttachments: boolean;
};

export default function AcknowledgeSafeReturnModal(props: AcknowledgeSafeReturnModalProps) {
  const { onCancel, onSuccess, patientName, locationEpisode, review } = props;
  const { profile } = useProfile();

  const invalidateQueries = useInvalidateQueries();

  const { mutate } = useMutateWithToast({
    mutationFn: updateReview,
    onSuccess: () => {
      invalidateQueries({
        locationEpisode: { id: locationEpisode.id },
        activities: true,
      });

      onSuccess();
      onCancel();
    },
  });

  const handleSubmit = async (
    values: FormValues<AcknowledgeSafeReturnFormValues>,
    _meta: FormMeta<AcknowledgeSafeReturnFormMeta>,
    helpers: FormHelpers<AcknowledgeSafeReturnFormValues, AcknowledgeSafeReturnFormMeta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    delete values.note?.escalationType;

    mutate(
      {
        id: review.id,
        reviewedById: profile.id,
        locationEpisodeId: locationEpisode.id,
        status: ReviewStatus.ACCEPTED,
        note: values.note
          ? {
              ...values.note,
              attachments: values.note.attachments?.map((upload) => upload.serialize()),
            }
          : undefined,
      },
      {
        onSettled: () => {
          helpers.setMeta('isSubmitting', false);
        },
      }
    );
  };

  return (
    <StandardModal title='Acknowledge Safe Return Request' onCancel={onCancel}>
      <PatientName>Patient: {patientName}</PatientName>

      <BodySmall>Acknowledging the safe return request will notify {locationEpisode.group.name}</BodySmall>

      <FormProvider<AcknowledgeSafeReturnFormValues, AcknowledgeSafeReturnFormMeta>
        onSubmit={handleSubmit}
        resetOnInitialValuesChange>
        <SafeReturnForm
          onCancel={onCancel}
          locationEpisode={locationEpisode}
          noteFieldLabel='Notes (optional)'
          submitButtonText='Acknowledge'
        />
      </FormProvider>
    </StandardModal>
  );
}
